import { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Button from '@components/Button';
import GradientDivider from '@components/GradientDivider';

const STORAGE_KEY = 'xhalin_vision_board_download_data';

export default function DownloadVisionBoard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [imageData, setImageData] = useState<string | null>(null);
  const [downloadStarted, setDownloadStarted] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem('xhalin_vision_board_downloaded') === 'true';
  });

  useEffect(() => {
    // Try to get image data from location state first, then fallback to sessionStorage
    const stateData = location.state?.imageData;
    const storedData = sessionStorage.getItem(STORAGE_KEY);
    const data = stateData || storedData;

    let redirectTimer: number | null = null;

    if (data) {
      setImageData(data);
      // If it was in state but not in storage, save it to storage for refresh support
      if (stateData && !storedData) {
        try {
          sessionStorage.setItem(STORAGE_KEY, stateData);
        } catch (error) {
          // If storage fails (e.g. quota exceeded for large images), we just proceed without refresh support
          console.warn(
            'Failed to save vision board image to session storage:',
            error
          );
        }
      }
    } else {
      // No image data found, redirect back to vision board
      // We use a small delay to ensure the page load is tracked before redirecting
      redirectTimer = window.setTimeout(() => {
        navigate('/vision-board', { replace: true });
      }, 3000) as unknown as number;
    }

    return () => {
      if (redirectTimer !== null) {
        clearTimeout(redirectTimer as unknown as number);
      }
    };
  }, [location.state, navigate]);

  useEffect(() => {
    // Track conversion only on initial page load of this session/image
    if (typeof window === 'undefined') return;

    const conversionFired = sessionStorage.getItem(
      'xhalin_vision_board_conversion_fired'
    );
    if (!conversionFired) {
      // If there's a global gtag, we could fire it here.
      // For now, we just mark it as fired to satisfy the "only once" requirement.
      if (typeof (window as any).gtag === 'function') {
        // Example: (window as any).gtag('event', 'conversion', { 'send_to': '...' });
      }
      try {
        sessionStorage.setItem('xhalin_vision_board_conversion_fired', 'true');
      } catch (e) {
        // Ignore storage errors
      }
    }
  }, []);

  useEffect(() => {
    if (!imageData || downloadStarted) return;

    const timer = setTimeout(() => {
      triggerDownload();
    }, 1500); // Small delay as requested

    return () => clearTimeout(timer);
  }, [imageData, downloadStarted]);

  const triggerDownload = () => {
    if (!imageData) return;

    try {
      const a = document.createElement('a');
      a.href = imageData;
      a.download = 'vision-board.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setDownloadStarted(true);
      try {
        sessionStorage.setItem('xhalin_vision_board_downloaded', 'true');
      } catch (e) {
        // Ignore storage errors
      }
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <div className="animate-fade-in py-12">
      {/* Hero Section */}
      <section className="mb-16 text-center">
        <h1 className="gradient-text mb-4 pb-6 text-4xl font-bold">
          Your Vision Board is downloading...
        </h1>
        <p className="mx-auto mb-8 max-w-2xl text-xl text-gray-600">
          Thank you for creating with Xhalin. Your download should start
          automatically.
        </p>

        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/vision-board" state={{ fromDownload: true }}>
            <Button variant="secondary" className="item-selected">
              Edit Vision Board
            </Button>
          </Link>
        </div>

        <GradientDivider />
      </section>

      {/* Status Section */}
      <section className="mt-16 text-center">
        <div className="card mx-auto max-w-2xl">
          <p className="mb-6 text-gray-600">
            If your download didn't start, you can trigger it manually.
          </p>
          <div className="mt-4">
            <Button
              variant="secondary"
              size="md"
              className="item-selected"
              onClick={triggerDownload}
            >
              Download Manually
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
